import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import api from '@/utils/api';
import { getUser, setUser, removeToken } from '@/utils/auth';
import { toast } from 'sonner';
import { ArrowLeft, Camera, User, Mail, Phone, MapPin, Save, Loader2, Trash2, Store, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';

const SellerProfilePage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const user = getUser();
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    bio: '',
    address: '',
    city: '',
    country: '',
    avatar_url: null
  });

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        bio: user.bio || '',
        address: user.address || '',
        city: user.city || '',
        country: user.country || '',
        avatar_url: user.avatar_url || null
      });
    }
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setIsLoadingProfile(true);
    try {
      const response = await api.get('/auth/me');
      const profileData = {
        name: response.data.name || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        bio: response.data.bio || '',
        address: response.data.address || '',
        city: response.data.city || '',
        country: response.data.country || '',
        avatar_url: response.data.avatar_url || null
      };
      setProfile(profileData);
      
      // Sync localStorage with fresh data from API
      const currentUser = getUser();
      if (currentUser) {
        setUser({ ...currentUser, ...response.data });
      }
    } catch (error) {
      // Use cached user data
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      const response = await api.put('/auth/profile', {
        name: profile.name,
        phone: profile.phone,
        bio: profile.bio,
        address: profile.address,
        city: profile.city,
        country: profile.country
      });
      
      // Update local storage
      const updatedUser = { ...user, ...response.data.user };
      setUser(updatedUser);
      
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setIsUploadingAvatar(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/auth/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Update profile with new avatar URL (add cache buster)
      const avatarUrlWithCacheBuster = `${response.data.avatar_url}${response.data.avatar_url.includes('?') ? '&' : '?'}v=${Date.now()}`;
      setProfile(prev => ({ ...prev, avatar_url: avatarUrlWithCacheBuster }));
      const updatedUser = { ...user, avatar_url: avatarUrlWithCacheBuster };
      setUser(updatedUser);
      
      // Refresh profile to ensure consistency
      await fetchProfile();
      
      toast.success('Profile photo updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const getAvatarUrl = () => {
    if (profile.avatar_url) {
      if (profile.avatar_url.startsWith('http')) {
        return profile.avatar_url;
      }
      return `${process.env.REACT_APP_BACKEND_URL || ''}${profile.avatar_url}`;
    }
    return null;
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    setIsDeleting(true);
    
    try {
      await api.delete('/auth/delete-account');
      toast.success('Your account has been deleted');
      removeToken();
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F6' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/seller/dashboard')}
            className="mb-2 -ml-2"
            style={{ color: '#5C635A' }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1
            className="text-3xl font-bold tracking-tight"
            style={{ fontFamily: 'Spectral, serif', color: '#163329' }}
          >
            My Profile
          </h1>
          <p className="mt-2" style={{ color: '#5C635A' }}>
            Manage your seller account information
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {/* Profile Photo Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:col-span-1"
          >
            <Card style={{ borderColor: '#E3E1DB' }}>
              <CardHeader>
                <CardTitle className="text-lg" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                  Profile Photo
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center">
                <div
                  className="relative w-32 h-32 rounded-full overflow-hidden cursor-pointer group"
                  onClick={handleAvatarClick}
                  style={{ backgroundColor: '#E3E1DB' }}
                >
                  {(isLoadingProfile || isUploadingAvatar) ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#163329' }} />
                    </div>
                  ) : getAvatarUrl() ? (
                    <img
                      src={getAvatarUrl()}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Store className="h-16 w-16" style={{ color: '#5C635A' }} />
                    </div>
                  )}
                  
                  {/* Overlay on hover */}
                  {!isLoadingProfile && !isUploadingAvatar && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Camera className="h-8 w-8 text-white" />
                    </div>
                  )}
                </div>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarChange}
                />
                
                <p className="mt-4 text-sm text-center" style={{ color: '#5C635A' }}>
                  Click to upload a new photo
                </p>
                <p className="text-xs" style={{ color: '#9CA3AF' }}>
                  Max 5MB, JPG/PNG
                </p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Profile Form Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="md:col-span-2"
          >
            <Card style={{ borderColor: '#E3E1DB' }}>
              <CardHeader>
                <CardTitle className="text-lg" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                  Account Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSaveProfile} className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="name" className="flex items-center gap-2">
                        <User className="h-4 w-4" style={{ color: '#5C635A' }} />
                        Full Name
                      </Label>
                      <Input
                        id="name"
                        value={profile.name}
                        onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="email" className="flex items-center gap-2">
                        <Mail className="h-4 w-4" style={{ color: '#5C635A' }} />
                        Email Address
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={profile.email}
                        disabled
                        className="mt-1 bg-gray-50"
                      />
                      <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>
                        Email cannot be changed
                      </p>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="phone" className="flex items-center gap-2">
                      <Phone className="h-4 w-4" style={{ color: '#5C635A' }} />
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      value={profile.phone}
                      onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                      placeholder="+1 (555) 000-0000"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="address" className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" style={{ color: '#5C635A' }} />
                      Address
                    </Label>
                    <Input
                      id="address"
                      value={profile.address}
                      onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                      placeholder="Street address"
                      className="mt-1"
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        value={profile.city}
                        onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                        placeholder="City"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="country">Country</Label>
                      <Input
                        id="country"
                        value={profile.country}
                        onChange={(e) => setProfile({ ...profile, country: e.target.value })}
                        placeholder="Country"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="bio">About / Bio</Label>
                    <Textarea
                      id="bio"
                      value={profile.bio}
                      onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                      placeholder="Tell us about yourself and your products..."
                      className="mt-1 min-h-[100px]"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={isSaving}
                    className="w-full sm:w-auto"
                    style={{ backgroundColor: '#163329' }}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Danger Zone */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-6"
        >
          <Card style={{ borderColor: '#FCA5A5' }}>
            <CardHeader>
              <CardTitle className="text-lg text-red-600 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Danger Zone
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm mb-4" style={{ color: '#5C635A' }}>
                Once you delete your account, there is no going back. This will permanently delete your account, 
                all your products, order history, and remove your data from our servers.
              </p>
              
              <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogTrigger asChild>
                  <Button variant="destructive">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Account
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="text-red-600 flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5" />
                      Delete Account
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <p className="text-sm" style={{ color: '#5C635A' }}>
                      This action is <strong>permanent and irreversible</strong>. All your data including:
                    </p>
                    <ul className="text-sm list-disc list-inside space-y-1" style={{ color: '#5C635A' }}>
                      <li>Your profile and account information</li>
                      <li>All products you have listed</li>
                      <li>Order history and transaction records</li>
                      <li>Messages and notifications</li>
                    </ul>
                    <p className="text-sm font-medium" style={{ color: '#163329' }}>
                      Type <span className="text-red-600 font-bold">DELETE</span> to confirm:
                    </p>
                    <Input
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="Type DELETE"
                      className="border-red-300"
                    />
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowDeleteDialog(false);
                          setDeleteConfirmText('');
                        }}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={handleDeleteAccount}
                        disabled={deleteConfirmText !== 'DELETE' || isDeleting}
                        className="flex-1"
                      >
                        {isDeleting ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          <>
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Forever
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default SellerProfilePage;
